import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

// Define types
interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  department?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        // Set default auth header
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Add timestamp to prevent caching issues
        const timestamp = new Date().getTime();
        
        // Verify token validity - try both paths
        try {
          const response = await api.get(`/api/auth/check?t=${timestamp}`);
          if (response.data.authenticated) {
            setIsAuthenticated(true);
            setUser(response.data.user);
          }
        } catch (err) {
          // Try alternative path
          const altResponse = await api.get(`/auth/check?t=${timestamp}`);
          if (altResponse.data.authenticated) {
            setIsAuthenticated(true);
            setUser(altResponse.data.user);
          } else {
            // Clear invalid token
            localStorage.removeItem('token');
          }
        }
      } catch (err) {
        console.error('Auth check error:', err);
        // Clear token on error
        localStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);
  
  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setUser(null);
  };
  
  // Login function
  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Attempting login with API URL:', api.defaults.baseURL);
      
      // Add timestamp to prevent caching issues
      const timestamp = new Date().getTime();
      
      // Try both API paths
      let response;
      try {
        // First try with /api prefix
        response = await api.post(`/api/auth/login?t=${timestamp}`, { email, password });
      } catch (err) {
        console.log('First login attempt failed, trying alternative path');
        // If that fails, try without /api prefix
        response = await api.post(`/auth/login?t=${timestamp}`, { email, password });
      }
      
      const { token, ...userData } = response.data;
      
      // Save token to localStorage
      localStorage.setItem('token', token);
      
      // Set default auth header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setIsAuthenticated(true);
      setUser(userData);
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Login failed. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Register function
  const register = async (name: string, email: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Attempting registration with API URL:', api.defaults.baseURL);
      
      // Add timestamp to prevent caching issues
      const timestamp = new Date().getTime();
      
      // Try both API paths
      let response;
      try {
        // First try with /api prefix
        response = await api.post(`/api/auth/register?t=${timestamp}`, { name, email, password });
      } catch (err) {
        console.log('First registration attempt failed, trying alternative path');
        // If that fails, try without /api prefix
        response = await api.post(`/auth/register?t=${timestamp}`, { name, email, password });
      }
      
      const { token, ...userData } = response.data;
      
      // Save token to localStorage
      localStorage.setItem('token', token);
      
      // Set default auth header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setIsAuthenticated(true);
      setUser(userData);
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Update user function
  const updateUser = async (userData: Partial<User>) => {
    setLoading(true);
    setError(null);
    
    try {
      // Try both API paths
      let response;
      try {
        // First try with /api prefix
        response = await api.patch('/api/auth/user', userData);
      } catch (err) {
        // If that fails, try without /api prefix
        response = await api.patch('/auth/user', userData);
      }
      
      setUser({ ...user, ...response.data });
    } catch (err: any) {
      console.error('Update user error:', err);
      setError(err.response?.data?.message || 'Failed to update user.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        error,
        login,
        register,
        logout,
        updateUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
